import React, {useEffect} from "react";
import {Grid, TextField} from "@material-ui/core";
import {Field} from "formik";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import {AppState} from "../../../store";
import {fillAgentFormField} from "../store/agent-form.slice";
import useAuthority from "app/hooks/useAuthority";
import {ROLES} from "app/constants/roles";
import {getAccount} from "app/account/store/get-account.slice";

interface AgentInformationProps {
    errors?: any;
    touched?: any;
}

const AgentInformation = ({errors = {}, touched = {}}: AgentInformationProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const agentForm = useSelector((state: AppState) => state.agents.agentForm);

    const {hasUserAnyAuthority} = useAuthority();
    const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);
    const isAgent = hasUserAnyAuthority([ROLES.ROLE_AGENT]) && !isAdmin;

    const {accountLoaded, accountCompanyName} = useSelector((state: AppState) => {
        const s: any = state as any;
        const account = s.account?.account?.account ?? null;

        return {
            accountLoaded: Boolean(account),
            accountCompanyName: account?.companyName ?? "",
        };
    });

    useEffect(() => {
        if (isAgent && !accountLoaded) {
            dispatch(getAccount() as any);
        }
    }, [isAgent, accountLoaded, dispatch]);

    useEffect(() => {
        if (isAgent && accountCompanyName && accountCompanyName.trim() !== "") {
            if (!agentForm.companyName || agentForm.companyName !== accountCompanyName) {
                dispatch(fillAgentFormField({field: "companyName", value: accountCompanyName}));
            }
        }
    }, [isAgent, accountCompanyName, agentForm.companyName, dispatch]);

    const handleChange = (e: any) => {
        dispatch(fillAgentFormField({field: e.target.name, value: e.target.value}));
    };

    const hasError = (field: string) => Boolean(touched[field] && errors[field]);

    const getError = (field: string) => {
        return touched[field] && errors[field] ? errors[field] : "";
    };

    return (
        <div>
            <Grid container spacing={4}>
                <Grid item xs={3}>
                    <Field
                        as={TextField}
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.nameLastname", "Full Name")}
                        onChange={handleChange}
                        value={agentForm.fullName}
                        variant="outlined"
                        size="small"
                        name="fullName"
                        required
                        error={hasError("fullName")}
                        helperText={getError("fullName")}
                    />
                </Grid>

                <Grid item xs={3}>
                    <Field
                        as={TextField}
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.nameofAgency", "Agency Name")}
                        onChange={handleChange}
                        value={agentForm.agencyName}
                        variant="outlined"
                        size="small"
                        name="agencyName"
                        required
                        error={hasError("agencyName")}
                        helperText={getError("agencyName")}
                    />
                </Grid>

                <Grid item xs={3}>
                    <Field
                        as={TextField}
                        style={{width: "100%"}}
                        label={t("app.main.employees.employeeList.table.phoneNumber", "Phone number")}
                        onChange={handleChange}
                        value={agentForm.phoneNumber}
                        variant="outlined"
                        size="small"
                        name="phoneNumber"
                        required
                        error={hasError("phoneNumber")}
                        helperText={getError("phoneNumber")}
                    />
                </Grid>

                <Grid item xs={3}>
                    <Field
                        as={TextField}
                        type="email"
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.email", "Contact E-mail")}
                        onChange={handleChange}
                        variant="outlined"
                        value={agentForm.contactEmail}
                        size="small"
                        name="contactEmail"
                        required
                        error={hasError("contactEmail")}
                        helperText={getError("contactEmail")}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={4}>
                    <Field
                        as={TextField}
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.companyName", "Company Name")}
                        onChange={handleChange}
                        value={agentForm.companyName}
                        variant="outlined"
                        size="small"
                        name="companyName"
                        required
                        disabled={isAgent}
                        error={hasError("companyName")}
                        helperText={getError("companyName")}
                    />
                </Grid>

                <Grid item xs={4}>
                    <Field
                        as={TextField}
                        type="email"
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.identifyEmail", "Identification (E-mail)")}
                        onChange={handleChange}
                        value={agentForm.email}
                        variant="outlined"
                        size="small"
                        name="email"
                        required
                        error={hasError("email")}
                        helperText={getError("email")}
                    />
                </Grid>

                <Grid item xs={4}>
                    <Field
                        as={TextField}
                        type="password"
                        style={{width: "100%"}}
                        label={t("app.main.employees.create.password", "Password")}
                        onChange={handleChange}
                        value={agentForm.password}
                        variant="outlined"
                        size="small"
                        name="password"
                        required
                        error={hasError("password")}
                        helperText={getError("password")}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Field
                        as={TextField}
                        style={{width: "100%"}}
                        label={t("app.main.agency.create.location", "Location")}
                        onChange={handleChange}
                        value={agentForm.location}
                        variant="outlined"
                        size="small"
                        name="location"
                        // required
                        // error={hasError("location")}
                        // helperText={getError("location")}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Field
                        as={TextField}
                        style={{width: "100%", marginBottom: "50px"}}
                        label={t("app.main.customer.customerList.address", "Address")}
                        onChange={handleChange}
                        value={agentForm.address}
                        variant="outlined"
                        size="small"
                        name="address"
                        // required
                        multiline
                        
                        rows={4}
                        // error={hasError("address")}
                        // helperText={getError("address")}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default AgentInformation;